import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { updateReservationCustomExportSetting } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import { InputField } from '@/components/molecules/input-field'
import * as CustomExportModel from '@/models/reservation/custom-export'
import { Controller, useForm } from 'react-hook-form'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'

type Props = {
  setting: CustomExportModel.Setting
  onCancel: () => void
  onUpdate: () => void
}

export const CustomExportFormatEditModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<{ title: string }>({
    defaultValues: {
      title: props.setting.title,
    },
  })

  const doUpdate = async formValues => {
    try {
      setIsLoading(true)
      await updateReservationCustomExportSetting(props.setting.id, { title: formValues.title })
      props.onUpdate()
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal customCss={modalStyle}>
      <ModalHeader>{t('Edit format name')}</ModalHeader>
      <ModalBody>
        <div className="form-container">
          <div>
            <Controller
              name="title"
              control={control}
              rules={{ required: t('Required field has not been entered') }}
              render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                <InputField label={t('Format name')} value={value} name={name} handleChangeData={onChange} error={error?.message} />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={props.onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          height="38px"
          width="110px"
          onClick={handleSubmit(doUpdate)}
        >
          {t('Save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  height: 'auto',
  width: 400,
  minWidth: 400,
  left: 'calc((100% - 400px) / 2)',
  '.form-container': {
    padding: 16,
    background: 'white',
    color: 'rgb(103, 103, 103)',
    border: '1px solid rgb(217, 217, 217)',
    borderRadius: 5,
  },
})
